import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Input,
  Form,
  Label,
  Button,
} from "reactstrap";
import moment from "moment";
import { userTransactionListApi } from "../../pages/User/_requests";
import TableContainer from "../../Components/Common/TableContainer";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { formatDate } from "../../helpers/FunctionHelpers";
import jsPDF from "jspdf";
import "jspdf-autotable";

interface Transaction {
  id: number;
  transaction_id: string;
  user_id: number;
  wallet_id: number;
  transaction_type: string;
  order_id: number | null;
  user_subscription_id: number | null;
  credit: number;
  debit: number;
  balance: string;
  payment_status: string;
  created_at: string;
  updated_at: string;
  user: {
    id: number;
    name: string;
    email: string;
  };
  order: {
    id: number;
    order_code_id: string;
    total_amount: string;
  } | null;
  userSubscription: {
    id: number;
    gross_amount: string;
    subscription?: {
      name?: string;
    }
  } | null;
}

interface TotalRow {
  id: string;
  transaction_id: string;
  created_at: string;
  transaction_type: string;
  user: { id: number; name: string; email: string };
  order: null;
  userSubscription: null;
  payment_status: string;
  amount: number;
  isTotal: boolean;
}

export function PaymentTransactionIndex() {
  const history = useNavigate();
  const { id } = useParams();
  const [allTransactions, setAllTransactions] = useState<Transaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [serverTotalAmount, setServerTotalAmount] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [filters, setFilters] = useState({
    userName: '',
    transactionType: 'all',
    payment_status: 'all',
    startDate: '',
    endDate: '',
  });

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const data = {
        user_id: "",
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        user_name: filters.userName,
        transaction_type: filters.transactionType,
        payment_status: filters.payment_status,
        start_date: filters.startDate,
        end_date: filters.endDate
      }
      const response = await userTransactionListApi(data);
      const resPayload = response?.data;
      const listData = Array.isArray(resPayload?.data)
        ? resPayload.data
        : (Array.isArray(resPayload) ? resPayload : (Array.isArray(response) ? response : []));
      setAllTransactions(listData);
      setFilteredTransactions(listData);
      setTotalCount(resPayload?.totalCount ?? listData.length);
      setServerTotalAmount(resPayload?.totalAmount ?? 0);

    } catch (error) {
      console.error("Error fetching transactions:", error);
      toast.error("Failed to fetch transactions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [pagination.pageIndex, pagination.pageSize, filters]); // Added filters to dependency array

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    // Reset to first page on filter change
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const applyFilters = () => {
    // With server-side filtering, we just trigger a refetch
    fetchTransactions();
  };

  const resetFilters = () => {
    setFilters({
      userName: '',
      transactionType: 'all',
      payment_status: 'all',
      startDate: '',
      endDate: '',
    });
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const getTotalAmount = (): number => {
    return serverTotalAmount;
  };

  document.title = "Paal Dappa | Admin panel";

  function goBack() {
    history(-1);
  }
  // Export Excel
  const exportExcel = () => {
    const data = filteredTransactions.map((txn: Transaction, index: number) => {
      const amount =
        txn.order?.total_amount ||
        txn.userSubscription?.gross_amount ||
        "0.00";

      return {
        "S.No": index + 1,
        "Transaction ID": txn.transaction_id,
        "Date & Time": formatDate(txn.created_at),
        "Type": txn.order_id ? "Order" : txn.user_subscription_id ? "Subscription" : "Other",
        User: txn.user?.name,
        Amount: parseFloat(amount).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
        Status: txn.payment_status,
      };
    });

    // Add Total row at bottom
    data.push({
      "S.No": 0,
      "Transaction ID": "",
      "Date & Time": "",
      "Type": "",
      User: "Grand Total",
      Amount: getTotalAmount().toLocaleString("en-IN", { minimumFractionDigits: 2 }),
      Status: "",
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Transactions");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, "transactions.xlsx");
  };
  const exportPDF = () => {
    const doc = new jsPDF();

    // Add title
    doc.setFontSize(16);
    doc.text("Transaction Report", 14, 15);
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 22);

    const tableData = filteredTransactions.map((txn: Transaction, index: number) => {
      // Ensure we clean the amount string to only numeric values
      const rawAmount = txn.order?.total_amount || txn.userSubscription?.gross_amount || "0";
      const cleanAmount = parseFloat(rawAmount.toString().replace(/[^0-9.-]+/g, "")) || 0;

      return [
        (index + 1).toString(),
        txn.transaction_id,
        formatDate(txn.created_at),
        txn.order_id ? "Order" : txn.user_subscription_id ? "Subscription" : "Other",
        txn.user?.name,
        `₹${cleanAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        txn.payment_status,
      ];
    });

    // Add total row
    const totalAmount = filteredTransactions.reduce((sum: number, txn: Transaction) => {
      const rawAmount = txn.order?.total_amount || txn.userSubscription?.gross_amount || "0";
      const cleanAmount = parseFloat(rawAmount.toString().replace(/[^0-9.-]+/g, "")) || 0;
      return sum + cleanAmount;
    }, 0);

    tableData.push([
      "",
      "",
      "",
      "",
      "Grand Total",
      `₹${totalAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
      "",
    ]);

    const tableColumns = [
      "S.No",
      "Transaction ID",
      "Date & Time",
      "Type",
      "User",
      "Amount",
      "Status",
    ];

    (doc as any).autoTable({
      head: [tableColumns],
      body: tableData,
      startY: 30,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [66, 135, 245] },
      footStyles: { fillColor: [66, 135, 245] },
      didDrawCell: (data: any) => {
        if (data.row.index === tableData.length - 1 && data.column.index === 4) {
          doc.setFillColor(230, 255, 230);
          doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, "F");
          doc.setTextColor(0, 128, 0);
          doc.setFont("helvetica", "bold");
        }
      },
    });

    doc.save("transactions.pdf");
  };

  const transactionsWithTotal = useMemo(() => {
    if (!filteredTransactions.length) return [];

    const total = getTotalAmount();

    return [
      ...filteredTransactions,
      {
        id: "total-row",
        transaction_id: "",
        created_at: "",
        transaction_type: "",
        user: { id: 0, name: "", email: "" },
        order: null,
        userSubscription: null,
        payment_status: "",
        amount: total,
        isTotal: true,
      } as TotalRow
    ];
  }, [filteredTransactions]);

  // Column definitions
  const columns = useMemo(
    () => [
      {
        header: "S.No",
        id: "serial",
        enableSorting: false,
        enableColumnFilter: false,
        cell: (cell: any) => {
          if (cell.row.original.isTotal) {
            return <strong className="text-success">Total</strong>;
          }
          const { pageIndex, pageSize } = cell.table.getState().pagination;
          const serialNumber = pageIndex * pageSize + cell.row.index + 1;
          return <span>{serialNumber}</span>;
        },
      },
      {
        header: "Transaction ID",
        accessorKey: "transaction_id",
      },
      {
        header: "Date & Time",
        accessorKey: "created_at",
        cell: ({ row }: any) => {
          if (row.original.isTotal) return "";
          return formatDate(row.original.created_at);
        },
      },
      {
        header: "Type",
        accessorKey: "transaction_type",
        cell: ({ row }: any) => {
          if (row.original.isTotal) return "";
          return row.original.transaction_type;
        },
      },
      {
        header: "User",
        accessorKey: "user.name",
        cell: ({ row }: any) => {
          if (row.original.isTotal) return "";
          return row.original.user.name;
        },
      },
      {
        header: "Reference",
        accessorKey: "reference",
        cell: ({ row }: any) => {
          if (row.original.isTotal) return "";
          const transaction = row.original;
          if (transaction.order_id) {
            return `Order: ${transaction.order?.order_code_id || ''}`;
          } else if (transaction.user_subscription_id) {
            return `Subscription: ${transaction.userSubscription?.subscription?.name || ''}`;
          }
          return "Other";
        },
      },
      {
        header: "Amount",
        accessorKey: "amount",
        cell: ({ row }: any) => {
          if (row.original.isTotal) {
            return (
              <strong className="text-success">
                Grand Total: ₹
                {parseFloat(row.original.amount).toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </strong>
            );
          }

          const transaction = row.original;
          const amount =
            transaction.order?.total_amount ||
            transaction.userSubscription?.gross_amount ||
            "0.00";
          return `₹${parseFloat(amount).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`;
        },
      },
      {
        header: "Status",
        accessorKey: "payment_status",
        enableColumnFilter: false,
        cell: ({ row }: any) => {
          const status = row.original.payment_status;
          const statusMap: Record<string, { class: string }> = {
            "COMPLETED": { class: "bg-success-subtle text-success" },
            "PENDING": { class: "bg-warning-subtle text-warning" },
            "FAILED": { class: "bg-danger-subtle text-danger" },
          };

          const statusClass = statusMap[status]?.class || "bg-secondary-subtle text-secondary";

          return (
            <span className={`badge fw-bold fs-7 ${statusClass}`}>
              {status}
            </span>
          );
        },
      },
    ],
    [filteredTransactions]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <ToastContainer closeButton={false} limit={1} />
          <BreadCrumb title="Transaction List" pageTitle="Transactions" />

          <Card>
            <CardHeader className="card-header border-0">
              <Row className="align-items-center gy-3">
                <div className="col-sm">
                  <h5 className="card-title mb-0">Transactions</h5>
                </div>
              </Row>
            </CardHeader>
            <CardBody>
              {/* Filters Section */}
              <Card className="mb-3">
                <CardBody>
                  <Form>
                    <Row className="gy-3">
                      {/* Transaction Type */}
                      <Col xs={12} sm={6} md={3}>
                        <div className="mb-3 mb-md-0">
                          <Label className="form-label d-block">Transaction Type</Label>
                          <div className="d-flex flex-wrap gap-3">
                            <div className="form-check">
                              <Input
                                type="radio"
                                id="transactionAll"
                                name="transactionType"
                                value="all"
                                checked={filters.transactionType === "all"}
                                onChange={handleFilterChange}
                                className="form-check-input"
                              />
                              <Label
                                htmlFor="transactionAll"
                                className="form-check-label"
                                style={{
                                  color: "#6c757d",
                                  fontWeight: filters.transactionType === "all" ? "bold" : "normal",
                                  fontSize: "16px",
                                }}
                              >
                                All
                              </Label>
                            </div>
                            <div className="form-check">
                              <Input
                                type="radio"
                                id="transactionOrder"
                                name="transactionType"
                                value="order"
                                checked={filters.transactionType === "order"}
                                onChange={handleFilterChange}
                                className="form-check-input"
                              />
                              <Label
                                htmlFor="transactionOrder"
                                className="form-check-label"
                                style={{
                                  color: "#0d6efd",
                                  fontWeight: filters.transactionType === "order" ? "bold" : "normal",
                                  fontSize: "16px",
                                }}
                              >
                                Order
                              </Label>
                            </div>
                            <div className="form-check">
                              <Input
                                type="radio"
                                id="transactionSubscription"
                                name="transactionType"
                                value="subscription"
                                checked={filters.transactionType === "subscription"}
                                onChange={handleFilterChange}
                                className="form-check-input"
                              />
                              <Label
                                htmlFor="transactionSubscription"
                                className="form-check-label"
                                style={{
                                  color: "#198754",
                                  fontWeight: filters.transactionType === "subscription" ? "bold" : "normal",
                                  fontSize: "16px",
                                }}
                              >
                                Subscription
                              </Label>
                            </div>
                            <div className="form-check">
                              <Input
                                type="radio"
                                id="transactionWallet"
                                name="transactionType"
                                value="wallet"
                                checked={filters.transactionType === "wallet"}
                                onChange={handleFilterChange}
                                className="form-check-input"
                              />
                              <Label
                                htmlFor="transactionWallet"
                                className="form-check-label"
                                style={{
                                  color: "#ffc107",
                                  fontWeight: filters.transactionType === "wallet" ? "bold" : "normal",
                                  fontSize: "16px",
                                }}
                              >
                                Wallet
                              </Label>
                            </div>
                          </div>
                        </div>
                      </Col>

                      {/* Status */}
                      <Col xs={12} sm={6} md={3}>
                        <div className="mb-3 mb-md-0">
                          <Label htmlFor="payment_status" className="form-label">
                            Status
                          </Label>
                          <Input
                            type="select"
                            id="payment_status"
                            name="payment_status"
                            value={filters.payment_status}
                            onChange={handleFilterChange}
                          >
                            <option value="all">All</option>
                            <option value="COMPLETED">Completed</option>
                            <option value="PENDING">Pending</option>
                            <option value="FAILED">Failed</option>
                          </Input>
                        </div>
                      </Col>

                      {/* Start Date */}
                      <Col xs={12} sm={6} md={3}>
                        <div className="mb-3 mb-md-0">
                          <Label htmlFor="startDate" className="form-label">
                            Start Date
                          </Label>
                          <Input
                            type="date"
                            id="startDate"
                            name="startDate"
                            value={filters.startDate}
                            onChange={handleFilterChange}
                          />
                        </div>
                      </Col>

                      {/* End Date */}
                      <Col xs={12} sm={6} md={3}>
                        <div className="mb-3 mb-md-0">
                          <Label htmlFor="endDate" className="form-label">
                            End Date
                          </Label>
                          <Input
                            type="date"
                            id="endDate"
                            name="endDate"
                            value={filters.endDate}
                            onChange={handleFilterChange}
                          />
                        </div>
                      </Col>

                      {/* User Name Filter */}
                      <Col xs={12} sm={6} md={3}>
                        <div className="mb-3 mb-md-0">
                          <Label htmlFor="userName" className="form-label">
                            User Name
                          </Label>
                          <Input
                            type="text"
                            id="userName"
                            name="userName"
                            value={filters.userName}
                            onChange={handleFilterChange}
                            placeholder="Search by user name"
                          />
                        </div>
                      </Col>

                      {/* Buttons Row */}
                      <Col xs={12} sm={6} md={3} className="d-flex flex-wrap justify-content-end gap-2">
                        <div className="mb-4 mb-md-0">
                          <Button color="primary me-3" onClick={applyFilters}>
                            Apply
                          </Button>
                          <Button color="secondary" onClick={resetFilters}>
                            Clear
                          </Button> </div>

                      </Col>
                    </Row>
                  </Form>
                </CardBody>
              </Card>

              <div>
                {loading ? (
                  <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  </div>
                ) : filteredTransactions?.length ? (
                  <>
                    {/* Download Buttons */}
                    <div className="d-flex justify-content-end gap-2 mt-3">
                      {/* <Button color="danger" onClick={exportPDF}>
                        Download PDF
                      </Button> */}
                      <Button color="success" onClick={exportExcel}>
                        Download Excel
                      </Button>
                    </div>
                    <TableContainer
                      columns={columns}
                      data={transactionsWithTotal}
                      isGlobalFilter={true}
                      customPageSize={pagination.pageSize}
                      manualPagination={true}
                      rowCount={totalCount}
                      pageCount={Math.ceil(totalCount / pagination.pageSize)}
                      onPaginationChange={setPagination}
                      paginationState={pagination}
                      divClass="table-responsive table-card mb-1 mt-0"
                      tableClass="align-top table-bordered align-top table-nowrap"
                      theadClass="table-light text-muted text-uppercase"
                    />
                  </>
                ) : (
                  <p className="fw-bold text-center">No Transactions Found</p>
                )}
              </div>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
}
